function getLogoContentBounds(pixelData, width, height) {
  const cornerPoints = [
    [0, 0],
    [width - 1, 0],
    [0, height - 1],
    [width - 1, height - 1],
  ];
  const average = [0, 0, 0, 0];

  for (const [x, y] of cornerPoints) {
    const index = (y * width + x) * 4;
    average[0] += pixelData[index];
    average[1] += pixelData[index + 1];
    average[2] += pixelData[index + 2];
    average[3] += pixelData[index + 3];
  }

  const background = average.map((value) => Math.round(value / cornerPoints.length));
  const backgroundIsTransparent = background[3] < 24;
  const alphaThreshold = backgroundIsTransparent ? 12 : 28;
  const colorTolerance = 84;
  const alphaTolerance = 24;

  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = (y * width + x) * 4;
      const red = pixelData[index];
      const green = pixelData[index + 1];
      const blue = pixelData[index + 2];
      const alpha = pixelData[index + 3];
      const colorDistance = Math.abs(red - background[0]) + Math.abs(green - background[1]) + Math.abs(blue - background[2]);
      const alphaDistance = Math.abs(alpha - background[3]);
      const isBackground = backgroundIsTransparent
        ? alpha <= alphaThreshold
        : alpha <= alphaThreshold || (alphaDistance <= alphaTolerance && colorDistance <= colorTolerance);

      if (!isBackground) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (maxX < minX || maxY < minY) {
    return null;
  }

  const bleed = 2;
  const left = Math.max(0, minX - bleed);
  const top = Math.max(0, minY - bleed);

  return {
    left,
    top,
    width: Math.min(width - left, (maxX - minX + 1) + (bleed * 2)),
    height: Math.min(height - top, (maxY - minY + 1) + (bleed * 2)),
  };
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

export async function prepareLogoPreviewAsset(src) {
  if (!src) {
    return { src: '', aspectRatio: 1 };
  }

  const image = await loadImage(src);
  const width = image.naturalWidth || image.width || 1;
  const height = image.naturalHeight || image.height || 1;
  const fallback = {
    src,
    aspectRatio: width / height,
  };

  try {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });

    if (!context) {
      return fallback;
    }

    context.drawImage(image, 0, 0, width, height);
    const imageData = context.getImageData(0, 0, width, height);
    const bounds = getLogoContentBounds(imageData.data, width, height);

    if (!bounds) {
      return fallback;
    }

    const coversWholeImage =
      bounds.left === 0 &&
      bounds.top === 0 &&
      bounds.width === width &&
      bounds.height === height;

    if (coversWholeImage) {
      return fallback;
    }

    const trimmedCanvas = document.createElement('canvas');
    trimmedCanvas.width = bounds.width;
    trimmedCanvas.height = bounds.height;
    const trimmedContext = trimmedCanvas.getContext('2d');

    if (!trimmedContext) {
      return fallback;
    }

    trimmedContext.drawImage(
      image,
      bounds.left,
      bounds.top,
      bounds.width,
      bounds.height,
      0,
      0,
      bounds.width,
      bounds.height
    );

    return {
      src: trimmedCanvas.toDataURL('image/png'),
      aspectRatio: bounds.width / bounds.height,
    };
  } catch (error) {
    return fallback;
  }
}
